import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import ValuationSummary from './components/ValuationSummary';
import ComparableSales from './components/ComparableSales';
import DepreciationChart from './components/DepreciationChart';
import AdjustmentNotes from './components/AdjustmentNotes';
import ExportControls from './components/ExportControls';

import Button from '../../components/ui/Button';

const CraneValuationReport = () => {
  const [userTier, setUserTier] = useState('free'); // free, pro, enterprise
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for crane specifications
  const craneData = {
    make: "Liebherr",
    model: "LTM 1090-4.2",
    year: 2018,
    hours: 3250,
    capacity: 90,
    serialNumber: "LTM1090-001234",
    condition: 4,
    location: "Dallas, TX"
  };

  // Mock valuation data
  const valuationData = {
    fairValue: {
      min: 485000,
      mid: 525000,
      max: 565000
    },
    confidenceScore: 87,
    daysOnMarket: 45,
    comparableCount: 11
  };

  // Mock comparable sales data
  const comparablesData = [
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2019,
      hours: 2890,
      capacity: 90,
      condition: 4,
      askingPrice: 545000,
      daysListed: 12,
      location: { city: "Houston", state: "TX", distance: 240 },
      dealer: { name: "Texas Crane Sales", phone: "(713) 555-0123" },
      source: "dealer",
      relevanceScore: 95
    },
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2017,
      hours: 4100,
      capacity: 90,
      condition: 3,
      askingPrice: 475000,
      daysListed: 28,
      location: { city: "Oklahoma City", state: "OK", distance: 190 },
      dealer: { name: "Southwest Equipment", phone: "(405) 555-0456" },
      source: "dealer",
      relevanceScore: 88
    },
    {
      make: "Liebherr",
      model: "LTM 1100-4.2",
      year: 2018,
      hours: 3500,
      capacity: 100,
      condition: 4,
      askingPrice: 580000,
      daysListed: 8,
      location: { city: "San Antonio", state: "TX", distance: 275 },
      dealer: { name: "Lone Star Cranes", phone: "(210) 555-0789" },
      source: "dealer",
      relevanceScore: 82
    },
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2016,
      hours: 5200,
      capacity: 90,
      condition: 3,
      askingPrice: 425000,
      daysListed: 45,
      location: { city: "Little Rock", state: "AR", distance: 320 },
      dealer: { name: "Arkansas Heavy Equipment", phone: "(501) 555-0234" },
      source: "auction",
      relevanceScore: 78
    },
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2019,
      hours: 2100,
      capacity: 90,
      condition: 5,
      askingPrice: 595000,
      daysListed: 5,
      location: { city: "Austin", state: "TX", distance: 195 },
      dealer: { name: "Capital City Cranes", phone: "(512) 555-0567" },
      source: "dealer",
      relevanceScore: 92
    },
    {
      make: "Tadano",
      model: "ATF 90G-4",
      year: 2018,
      hours: 3800,
      capacity: 90,
      condition: 4,
      askingPrice: 510000,
      daysListed: 22,
      location: { city: "Tulsa", state: "OK", distance: 245 },
      dealer: { name: "Midwest Crane Co", phone: "(918) 555-0890" },
      source: "dealer",
      relevanceScore: 75
    },
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2020,
      hours: 1850,
      capacity: 90,
      condition: 5,
      askingPrice: 625000,
      daysListed: 3,
      location: { city: "Fort Worth", state: "TX", distance: 35 },
      dealer: { name: "DFW Equipment Sales", phone: "(817) 555-0345" },
      source: "dealer",
      relevanceScore: 96
    },
    {
      make: "Liebherr",
      model: "LTM 1090-4.2",
      year: 2017,
      hours: 4500,
      capacity: 90,
      condition: 3,
      askingPrice: 455000,
      daysListed: 38,
      location: { city: "Shreveport", state: "LA", distance: 185 },
      dealer: { name: "Louisiana Crane Sales", phone: "(318) 555-0678" },
      source: "auction",
      relevanceScore: 85
    }
  ];

  // Mock depreciation data
  const depreciationData = [
    { year: 2015, value: 750000 },
    { year: 2016, value: 685000 },
    { year: 2017, value: 625000 },
    { year: 2018, value: 570000 },
    { year: 2019, value: 520000 },
    { year: 2020, value: 475000 },
    { year: 2021, value: 435000 },
    { year: 2022, value: 400000 },
    { year: 2023, value: 370000 },
    { year: 2024, value: 345000 }
  ];

  // Mock adjustment notes
  const adjustmentNotes = [
    {
      factor: "Low Hours Adjustment",
      type: "positive",
      impact: 8,
      explanation: "Below average hours for model year increases value due to reduced wear and extended remaining useful life."
    },
    {
      factor: "Regional Market Premium",
      type: "positive",
      impact: 5,
      explanation: "Texas market shows strong demand for mobile cranes with premium pricing compared to national average."
    },
    {
      factor: "Model Popularity",
      type: "positive",
      impact: 3,
      explanation: "LTM 1090-4.2 is a popular model with strong resale value and high dealer demand."
    },
    {
      factor: "Age Depreciation",
      type: "negative",
      impact: -12,
      explanation: "Standard age-based depreciation applied based on 6-year age and industry depreciation curves."
    }
  ];

  // Mock methodology data
  const methodologyData = {
    baseProcess: `Our valuation process combines market-based analysis with proprietary algorithms to determine fair market value. We analyze comparable sales, auction results, and dealer listings to establish value ranges with confidence scoring based on data quality and market conditions.`,
    dataSources: [
      "Dealer inventory listings",
      "Auction sale results",
      "Private party transactions",
      "OEM pricing data",
      "Insurance claim settlements"
    ],
    weightingFactors: [
      { name: "Recent comparable sales", weight: 40 },
      { name: "Current market listings", weight: 25 },
      { name: "Auction results", weight: 20 },
      { name: "Regional adjustments", weight: 10 },
      { name: "Condition factors", weight: 5 }
    ],
    confidenceFactors: {
      positive: [
        "Multiple recent comparable sales",
        "Consistent pricing across sources",
        "Active market with regular transactions",
        "Detailed specification matching"
      ],
      negative: [
        "Limited recent sales data",
        "Wide price variation in comparables",
        "Seasonal market fluctuations",
        "Unique configuration or options"
      ]
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Search Results', path: '/crane-search-dashboard?results=true' },
    { label: 'Valuation Report' }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleExport = async (format) => {
    // Simulate export process
    console.log(`Exporting report as ${format}`);
    return new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleSave = async () => {
    // Simulate save process
    console.log('Saving valuation report');
    return new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleNewSearch = () => {
    window.location.href = '/crane-search-dashboard';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">Generating Valuation Report</h2>
            <p className="text-text-secondary">Analyzing comparable sales and market data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="container mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Breadcrumbs items={breadcrumbItems} className="mb-2" />
            <h1 className="text-3xl font-bold text-text-primary">Crane Valuation Report</h1>
            <p className="text-text-secondary mt-1">
              Comprehensive fair market value analysis for {craneData?.make} {craneData?.model}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <SubscriptionTierIndicator tier={userTier} showUpgrade={userTier === 'free'} />
            <Button
              variant="outline"
              onClick={handleNewSearch}
              iconName="Search"
              iconPosition="left"
            >
              New Search
            </Button>
          </div>
        </div>

        {/* Report Content */}
        <div className="space-y-8">
          {/* Valuation Summary */}
          <ValuationSummary 
            craneData={craneData} 
            valuationData={valuationData} 
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Comparables and Chart */}
            <div className="xl:col-span-2 space-y-8">
              <ComparableSales 
                comparables={comparablesData} 
                isBlurred={userTier === 'free'} 
              />
              <DepreciationChart 
                depreciationData={depreciationData} 
                isBlurred={userTier === 'free'} 
              />
            </div>

            {/* Right Column - Adjustments and Export */}
            <div className="space-y-8">
              <AdjustmentNotes 
                adjustments={adjustmentNotes} 
                methodology={methodologyData} 
              />
              <ExportControls 
                userTier={userTier} 
                onExport={handleExport} 
                onSave={handleSave} 
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-sm text-text-secondary">
              Report generated on {new Date()?.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })} • Valid for 30 days
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => window.location.href = '/comparison-tool'}
                iconName="GitCompare"
                iconPosition="left"
              >
                Compare Models
              </Button>
              <Button
                variant="default"
                onClick={handleNewSearch}
                iconName="Search"
                iconPosition="left"
              >
                Search Again
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CraneValuationReport;